import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * Bouton d'opt-in notifications push.
 * - Vérifie la support du navigateur
 * - Demande la clé VAPID publique au backend
 * - Enregistre la subscription côté backend
 */
export default function PushOptInButton({ cities = [], categories = [], className = "", compact = false }) {
  const [status, setStatus] = useState("unknown"); // unknown | denied | granted | unsupported | subscribed
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (typeof window === "undefined") return;
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
        setStatus("unsupported"); return;
      }
      if (Notification.permission === "denied") { setStatus("denied"); return; }
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        setStatus(sub ? "subscribed" : "granted_or_default");
      } catch { setStatus("granted_or_default"); }
    };
    check();
  }, []);

  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/push/vapid-key");
      if (!data?.public_key) { toast.error("Notifications indisponibles pour l'instant."); return; }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.info("Vous n'avez pas autorisé les notifications."); setStatus("denied"); return; }
      let reg = await navigator.serviceWorker.getRegistration();
      if (!reg) reg = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
      await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      await api.post("/me/push/subscribe", {
        endpoint: sub.endpoint,
        keys: { p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey("p256dh")))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""),
                 auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey("auth")))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "") },
        cities, categories,
      });
      setStatus("subscribed");
      toast.success("Notifications activées !");
    } catch (e) {
      toast.error(e?.message || "Impossible d'activer les notifications.");
    } finally { setLoading(false); }
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api.post("/me/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setStatus("granted_or_default");
      toast.success("Notifications désactivées");
    } catch { toast.error("Erreur."); }
    finally { setLoading(false); }
  };

  if (status === "unsupported") return null;
  if (status === "denied") {
    return (
      <div className={`inline-flex items-center gap-2 text-xs text-slate-500 ${className}`} data-testid="push-denied">
        <BellOff size={13}/> Notifications bloquées — réactivez-les dans les réglages du navigateur.
      </div>
    );
  }

  const subscribed = status === "subscribed";
  const Btn = (
    <button onClick={subscribed ? unsubscribe : subscribe} disabled={loading}
            data-testid={subscribed ? "push-unsubscribe" : "push-subscribe"}
            className={`inline-flex items-center gap-1.5 rounded-full font-bold text-xs transition-colors ${compact ? "px-2.5 py-1" : "px-3 py-1.5"} ${
              subscribed ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border border-emerald-200"
                         : "bg-[#E05638] text-white hover:bg-[#C13E1F]"
            } disabled:opacity-50 ${className}`}>
      {loading ? <Loader2 size={13} className="animate-spin"/> : (subscribed ? <Bell size={13}/> : <Bell size={13}/>)}
      {subscribed ? "Notifications actives" : "Activer les notifications"}
    </button>
  );
  return Btn;
}
